import { ChevronDown } from 'lucide-react'

import { cn } from '@/utils'
import { Popover, PopoverTrigger } from '@/components/ui/popover'
import { Slider } from '@/components/ui/slider'
import { InputWithSuffix } from '@/components/ui/input-group'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import {
  TEXT_ON_PATH_OFFSET_MAX,
  TEXT_ON_PATH_OFFSET_MIN,
  TEXT_ON_PATH_OFFSET_PRESETS,
} from '@/lib/shapes/text-on-path-session'

import { TRIGGER_CLASS } from '../constants/format-control-classes'
import { useNumericFieldInput } from '../hooks/use-numeric-field-input'
import type { ControlProps } from '../types/control-props'
import { FormatPopoverContent } from './format-popover-content'

/**
 * Perpendicular distance from the path (WMPCM-9535, Story 4 / wire `TO`).
 *
 * Laid out to match the Figma control directly (node `20459:492247`): a `Text Offset` label, a
 * Narrow / Natural / Wide toggle row, then a fixed-width slider paired with a `pt` input, spread
 * to the popover's edges. `Natural` (0) is the reset target — see `TEXT_ON_PATH_OFFSET_PRESETS`.
 *
 * Not built on `NumericPresetField` like Size/Tracking/Kerning are, even though the parts list is
 * the same. When this landed, that component was styled a good deal tighter than this spec and is
 * shared with three other live controls, so bending it to this Figma would have restyled all of
 * them. The Figma parity pass has since moved `NumericPresetField` onto these same metrics
 * (`text-sm` foreground label, `h-9` chips), so the sizing half of that argument no longer holds —
 * folding this control into it is now a reasonable follow-up, and would delete ~90 lines. It is
 * deliberately NOT done as part of a rebase: `text-on-path-offset-control.test.tsx` asserts
 * against this markup. (The input state machine underneath IS shared — `useNumericFieldInput`.)
 *
 * The pieces reused instead are the primitives the spec actually names — the project's
 * `ToggleGroup` at its default size is already Figma's toggle (`h-9 px-2`, `text-sm`, transparent,
 * `rounded-md`), `Slider` is already its slider (6px track, 16px thumb), and `PopoverContent`'s own
 * `p-4 rounded-md border shadow-md` is already its frame — plus the shared `validateNumericField`
 * ladder, so this field's accepted values and error copy cannot drift from the other three.
 */
export function TextOnPathOffsetControl({
  config,
  onConfigChange,
  onRestoreFocus,
  className,
}: ControlProps) {
  const { inputValue, error, commit, handleFocus, handleChange, handleBlur } = useNumericFieldInput(
    config.offset,
    TEXT_ON_PATH_OFFSET_MIN,
    TEXT_ON_PATH_OFFSET_MAX,
    (next) => onConfigChange({ offset: next })
  )
  const errorId = 'text-on-path-offset-error'
  const activePreset = TEXT_ON_PATH_OFFSET_PRESETS.find((preset) => preset.value === config.offset)

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" aria-label="Offset" className={cn(TRIGGER_CLASS, className)}>
          Offset
          <ChevronDown className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      {/* 320px = Figma's 288px content column inside `PopoverContent`'s own 16px padding. */}
      <FormatPopoverContent className="w-80" onRestoreFocus={onRestoreFocus}>
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium leading-none text-foreground">Text Offset</span>
          <ToggleGroup
            type="single"
            value={activePreset != null ? String(activePreset.value) : ''}
            onValueChange={(next) => {
              if (!next) return
              commit(Number(next))
            }}
            className="w-full justify-start gap-2"
          >
            {TEXT_ON_PATH_OFFSET_PRESETS.map((preset) => (
              <ToggleGroupItem key={preset.label} value={String(preset.value)}>
                {preset.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <div className="flex w-full items-start justify-between">
            <Slider
              min={TEXT_ON_PATH_OFFSET_MIN}
              max={TEXT_ON_PATH_OFFSET_MAX}
              step={1}
              value={[config.offset]}
              onValueChange={([next]) => commit(next ?? config.offset)}
              aria-label="Offset"
              className="mt-2.5 w-[130px] flex-none"
            />
            <div className="flex flex-col">
              <InputWithSuffix
                size="compact"
                type="text"
                value={inputValue}
                error={error != null}
                aria-label="Offset value"
                aria-describedby={error != null ? errorId : undefined}
                // `compact` is the sibling controls' width/height (h-9, 100px — Figma's input box
                // exactly) but dims to `text-xs text-muted-foreground`; the spec's value is
                // full-contrast `text-sm`.
                className="text-sm text-foreground"
                suffix={<span className="pl-1 text-xs text-muted-foreground">pt</span>}
                onFocus={handleFocus}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {error != null && (
                <span id={errorId} role="alert" className="mt-1 text-xs text-red-500">
                  {error}
                </span>
              )}
            </div>
          </div>
        </div>
      </FormatPopoverContent>
    </Popover>
  )
}
